import { useSyncExternalStore } from 'react';
import { DEFAULT_BAR_KEYS } from '@/shell/navDefs';

// Which tools the user has chosen for the bottom bar (beside a fixed Home and
// a fixed More). v1 persists in localStorage; useSyncExternalStore keeps the
// bottom bar and the Settings editor in sync live.
// TODO: move to a server-side per-user prefs endpoint so the choice follows a
// user across the shared back-of-house tablet and their phone.

const KEY = 'sweetspot:nav:bar-keys';
const listeners = new Set<() => void>();

// getSnapshot MUST return a referentially stable value between real changes,
// or useSyncExternalStore loops forever ("Maximum update depth exceeded",
// React #185). Cache the parsed array keyed on the raw stored string and
// return the same reference until the stored string actually changes.
let cachedRaw: string | null | undefined;
let cachedKeys: string[] = DEFAULT_BAR_KEYS;

function read(): string[] {
  let raw: string | null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return DEFAULT_BAR_KEYS;
  }
  if (raw === cachedRaw) return cachedKeys;
  cachedRaw = raw;
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const keys = parsed.filter((x): x is string => typeof x === 'string');
        if (keys.length > 0) {
          cachedKeys = keys;
          return cachedKeys;
        }
      }
    } catch {
      // ignore malformed storage
    }
  }
  cachedKeys = DEFAULT_BAR_KEYS;
  return cachedKeys;
}

export function setBarKeys(keys: string[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(keys));
  } catch {
    // ignore storage write failures (private mode, quota)
  }
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) cb();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
}

export function useBarKeys(): string[] {
  return useSyncExternalStore(subscribe, read, () => DEFAULT_BAR_KEYS);
}
